import React, { useState, useRef, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { VariableSizeList as List } from 'react-window';
import { AutoSizer } from 'react-virtualized';
import debounce from 'lodash.debounce';
import { actions } from '../../../../../../context';

const VirtualTableContext = React.createContext({
    top: 0,
    setTop: () => {},
    header: null,
    footer: null,
});

/**
 * Virtual table: variable-size list rendered as a table with a single sticky "table" layer.
 * Uses AutoSizer (react-virtualized) so the list always gets the correct container dimensions.
 */
function VirtualTable({ row, header, footer, height: heightProp, codeCount, ...listProps }) {
    const listRef = useRef(null);
    const [top, setTop] = useState(0);
    const dispatch = useDispatch();
    const skipCodesTableScrollToTop = useSelector((s) => s.daylight.skipCodesTableScrollToTop);

    const handleResize = debounce(() => {
        if (listRef.current) listRef.current.resetAfterIndex(0, true);
    }, 100);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!listRef.current) return;
        if (skipCodesTableScrollToTop) {
            dispatch(actions.daylight.clearSkipCodesTableScrollToTop());
            return;
        }
        listRef.current.scrollTo(0);
    }, [codeCount]);

    const handleItemsRendered = (props) => {
        const style = listRef.current?._getItemStyle(props.overscanStartIndex);
        setTop(style?.top ?? 0);
        listProps.onItemsRendered?.(props);
    };

    return (
        <VirtualTableContext.Provider value={{ top, setTop, header, footer }}>
            <div
                style={{
                    height: '100%',
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <AutoSizer>
                    {({ width, height }) => {
                        const listHeight = typeof heightProp === 'number' ? heightProp : height;
                        const listWidth = width ?? '100%';
                        if (!listHeight || listHeight <= 0) return null;
                        return (
                            <List
                                {...listProps}
                                ref={listRef}
                                width={listWidth}
                                height={listHeight}
                                innerElementType={Inner}
                                onItemsRendered={handleItemsRendered}
                            >
                                {row}
                            </List>
                        );
                    }}
                </AutoSizer>
            </div>
        </VirtualTableContext.Provider>
    );
}

const Inner = React.forwardRef(function Inner({ children, ...rest }, ref) {
    const { header, footer, top } = useContext(VirtualTableContext);
    return (
        <div
            {...rest}
            ref={ref}
            style={{ ...rest.style, position: 'relative', contain: 'layout' }}
        >
            <table
                className="fixed-table w-100 bg-light text-dark sticky-top border-bottom text-12"
                style={{
                    position: 'absolute',
                    top,
                    left: 0,
                    width: '100%',
                    margin: 0,
                }}
            >
                {header}
                <tbody>{children}</tbody>
                {footer}
            </table>
        </div>
    );
});

export default VirtualTable;
